import * as React from 'react';
import { BellFilled, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import { Badge, Button, Empty, List, Modal, Popconfirm, Popover, Space, Spin, Tag, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useCan } from '../../context/auth_atoms';
import { NotificationDetailPanel } from './NotificationDetailPanel';
import { notificationHasRelatedTarget, notificationRelatedLinkLabel, resolveNotificationRelatedLink } from './resolveNotificationLink';
import {
    useDeleteAllNotificationsMutation,
    useDeleteNotificationMutation,
    useInboxQuery,
    useMarkNotificationReadMutation,
    useUnreadCountQuery,
} from './queryHooks';
import type { InboxItem } from './types';

type Props = {
    style?: React.CSSProperties;
};

function InboxToolbar({
    itemCount,
    deleteAllLoading,
    onDeleteAll,
}: {
    itemCount: number;
    deleteAllLoading: boolean;
    onDeleteAll: () => void;
}) {
    return (
        <Popconfirm
            title="Delete all notifications?"
            description="This removes every notification from your inbox."
            okText="Delete all"
            okButtonProps={{ danger: true }}
            disabled={itemCount === 0 || deleteAllLoading}
            onConfirm={onDeleteAll}
        >
            <Button
                type="link"
                size="small"
                danger
                disabled={itemCount === 0 || deleteAllLoading}
                style={{ padding: 0, height: 'auto' }}
            >
                Delete all
            </Button>
        </Popconfirm>
    );
}

function NotificationInboxList({
    items,
    selectedId,
    loading,
    onSelect,
    onDelete,
    maxHeight,
}: {
    items: InboxItem[];
    selectedId?: number | null;
    loading: boolean;
    onSelect: (item: InboxItem) => void;
    onDelete?: (item: InboxItem) => void;
    maxHeight: number | string;
}) {
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
            </div>
        );
    }
    if (!items.length) {
        return <Empty description="No notifications" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }
    return (
        <List
            size="small"
            dataSource={items}
            style={{ overflow: 'auto', maxHeight }}
            renderItem={(item) => {
                const selected = selectedId === item.id;
                const read = item.read;
                return (
                    <List.Item
                        style={{
                            cursor: 'pointer',
                            background: selected
                                ? '#e8f0fe'
                                : read
                                  ? 'transparent'
                                  : '#f6f9fe',
                            paddingInline: 10,
                            paddingBlock: 8,
                            borderRadius: 6,
                            marginBottom: 4,
                            borderLeft: read ? '3px solid transparent' : '3px solid #1a73e8',
                        }}
                        onClick={() => onSelect(item)}
                        actions={
                            onDelete
                                ? [
                                      <Button
                                          key="delete"
                                          type="text"
                                          size="small"
                                          aria-label="Delete notification"
                                          icon={<DeleteOutlined />}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              onDelete(item);
                                          }}
                                      />,
                                  ]
                                : undefined
                        }
                    >
                        <List.Item.Meta
                            title={(
                                <Typography.Text
                                    ellipsis
                                    strong={!read}
                                    style={{
                                        color: read ? '#5f6368' : '#202124',
                                        fontWeight: read ? 400 : 600,
                                    }}
                                >
                                    {item.title}
                                </Typography.Text>
                            )}
                            description={(
                                <>
                                    <Typography.Paragraph
                                        ellipsis={{ rows: 2 }}
                                        style={{
                                            marginBottom: 4,
                                            fontSize: 12,
                                            color: read ? '#80868b' : '#3c4043',
                                        }}
                                    >
                                        {item.body.split('\n')[0]}
                                    </Typography.Paragraph>
                                    <Typography.Text
                                        type="secondary"
                                        style={{ fontSize: 11, color: read ? '#9aa0a6' : undefined }}
                                    >
                                        {new Date(item.created_at).toLocaleString()}
                                    </Typography.Text>
                                </>
                            )}
                        />
                    </List.Item>
                );
            }}
        />
    );
}

export function NotificationBell({ style }: Props) {
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const [inboxModalOpen, setInboxModalOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState<number | null>(null);
    const [resolvingRelatedLink, setResolvingRelatedLink] = React.useState(false);
    const navigate = useNavigate();
    const can = useCan();
    const isAdmin = can('read', 'admin::data');
    const inboxLimit = 20;

    const inboxEnabled = popoverOpen || inboxModalOpen;
    const { data: unreadCount = 0, isLoading: countLoading } = useUnreadCountQuery();
    const { data: inbox, isLoading: inboxLoading, refetch } = useInboxQuery(inboxLimit, inboxEnabled);
    const markRead = useMarkNotificationReadMutation(inboxLimit);
    const deleteOne = useDeleteNotificationMutation(inboxLimit);
    const deleteAll = useDeleteAllNotificationsMutation();

    const items = inbox?.items ?? [];
    const selectedItem =
        selectedId != null ? items.find((n) => n.id === selectedId) ?? null : null;
    const showRelatedAction = selectedItem ? notificationHasRelatedTarget(selectedItem) : false;

    const onPopoverOpenChange = (next: boolean) => {
        setPopoverOpen(next);
        if (next) {
            void refetch();
        }
    };

    const closeInboxModal = () => {
        setInboxModalOpen(false);
        setSelectedId(null);
    };

    const openLink = (link: string) => {
        setPopoverOpen(false);
        closeInboxModal();
        if (link.startsWith('http://') || link.startsWith('https://')) {
            window.open(link, '_blank', 'noopener,noreferrer');
        } else {
            navigate(link);
        }
    };

    const openRelatedForItem = async (item: InboxItem) => {
        setResolvingRelatedLink(true);
        try {
            const path = await resolveNotificationRelatedLink(item);
            if (path) {
                openLink(path);
            }
        } finally {
            setResolvingRelatedLink(false);
        }
    };

    const selectItem = async (item: InboxItem) => {
        setSelectedId(item.id);
        if (!item.read) {
            await markRead.mutateAsync(item.id);
        }
    };

    const pickSelectionAfterRemoval = (removedId: number, nextItems: InboxItem[]) => {
        if (selectedId !== removedId) {
            return;
        }
        if (nextItems.length === 0) {
            setSelectedId(null);
            return;
        }
        const idx = items.findIndex((n) => n.id === removedId);
        const fallback = nextItems[Math.min(idx, nextItems.length - 1)];
        setSelectedId(fallback?.id ?? null);
    };

    const deleteItem = async (item: InboxItem) => {
        await deleteOne.mutateAsync(item.id);
        const nextItems = items.filter((n) => n.id !== item.id);
        pickSelectionAfterRemoval(item.id, nextItems);
        if (nextItems.length === 0 && inboxModalOpen) {
            closeInboxModal();
        }
    };

    const deleteAllItems = async () => {
        await deleteAll.mutateAsync();
        setSelectedId(null);
        closeInboxModal();
        setPopoverOpen(false);
    };

    const openInboxModal = async (item: InboxItem) => {
        setPopoverOpen(false);
        setInboxModalOpen(true);
        await selectItem(item);
    };

    React.useEffect(() => {
        if (!inboxModalOpen || inboxLoading || items.length === 0) {
            return;
        }
        if (selectedId == null || !items.some((n) => n.id === selectedId)) {
            void selectItem(items[0]);
        }
    }, [inboxModalOpen, inboxLoading, items, selectedId]);

    const popoverContent = (
        <div style={{ width: 360, maxHeight: 440, display: 'flex', flexDirection: 'column' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                }}
            >
                <Typography.Text strong>Notifications</Typography.Text>
                <InboxToolbar
                    itemCount={items.length}
                    deleteAllLoading={deleteAll.isLoading}
                    onDeleteAll={() => void deleteAllItems()}
                />
            </div>
            <NotificationInboxList
                items={items}
                loading={inboxLoading}
                onSelect={(item) => void openInboxModal(item)}
                onDelete={(item) => void deleteItem(item)}
                maxHeight={340}
            />
            {isAdmin && (
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 8 }}>
                    <Link to="/admin/notifications" onClick={() => setPopoverOpen(false)}>
                        Send notification…
                    </Link>
                </div>
            )}
        </div>
    );

    const listPaneHeight = 'min(520px, calc(70vh - 120px))';

    return (
        <>
            <Popover
                content={popoverContent}
                trigger="click"
                placement="bottomRight"
                open={popoverOpen}
                onOpenChange={onPopoverOpenChange}
                overlayInnerStyle={{ padding: 12 }}
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', ...style }}>
                    <Badge count={countLoading ? 0 : unreadCount} size="small" offset={[-2, 2]}>
                        <BellFilled style={{ fontSize: 18 }} />
                    </Badge>
                </span>
            </Popover>
            <Modal
                open={inboxModalOpen}
                onCancel={closeInboxModal}
                width={1000}
                footer={null}
                destroyOnClose
                styles={{ body: { padding: 0 } }}
                title={(
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingRight: 32,
                            gap: 12,
                        }}
                    >
                        <span>Notifications</span>
                        <Space size={8} wrap align="center">
                            <InboxToolbar
                                itemCount={items.length}
                                deleteAllLoading={deleteAll.isLoading}
                                onDeleteAll={() => void deleteAllItems()}
                            />
                            {isAdmin && (
                                <Link to="/admin/notifications" onClick={closeInboxModal}>
                                    Send notification…
                                </Link>
                            )}
                        </Space>
                    </div>
                )}
            >
                <div style={{ display: 'flex', minHeight: 420, maxHeight: '70vh' }}>
                    <div
                        style={{
                            width: 320,
                            flexShrink: 0,
                            borderRight: '1px solid #f0f0f0',
                            padding: '12px 12px 12px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }}
                    >
                        <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
                            {items.length} notification{items.length === 1 ? '' : 's'}
                        </Typography.Text>
                        <NotificationInboxList
                            items={items}
                            selectedId={selectedId}
                            loading={inboxLoading}
                            onSelect={(item) => void selectItem(item)}
                            onDelete={(item) => void deleteItem(item)}
                            maxHeight={listPaneHeight}
                        />
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }}
                    >
                        {inboxLoading && !selectedItem ? (
                            <div style={{ textAlign: 'center', paddingTop: 80, flex: 1 }}>
                                <Spin />
                            </div>
                        ) : !selectedItem ? (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Empty description="Select a notification" />
                            </div>
                        ) : (
                            <>
                                <div
                                    style={{
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #f0f0f0',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 12,
                                    }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Typography.Title
                                            level={5}
                                            style={{
                                                margin: 0,
                                                color: selectedItem.read ? '#5f6368' : '#202124',
                                                fontWeight: selectedItem.read ? 400 : 600,
                                            }}
                                            ellipsis
                                        >
                                            {selectedItem.title}
                                        </Typography.Title>
                                    </div>
                                    <Space size={8} wrap style={{ flexShrink: 0 }}>
                                        {!selectedItem.read && <Tag color="blue">Unread</Tag>}
                                        {showRelatedAction && (
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<LinkOutlined />}
                                                loading={resolvingRelatedLink}
                                                onClick={() => void openRelatedForItem(selectedItem)}
                                            >
                                                {notificationRelatedLinkLabel(selectedItem)}
                                            </Button>
                                        )}
                                        <Popconfirm
                                            title="Delete this notification?"
                                            okText="Delete"
                                            okButtonProps={{ danger: true }}
                                            onConfirm={() => void deleteItem(selectedItem)}
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                loading={deleteOne.isLoading}
                                            >
                                                Delete
                                            </Button>
                                        </Popconfirm>
                                    </Space>
                                </div>
                                <div style={{ padding: '16px 24px 24px', overflow: 'auto', flex: 1 }}>
                                    <NotificationDetailPanel item={selectedItem} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
}
